import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import EmojiPicker from "emoji-picker-react";
import WhatsAppPreview from "./WhatsAppPreview";
import api from "../../../core/services/api";
import { templateService } from "../../services";

const META_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "en_US", label: "English (US)" },
  { code: "en_GB", label: "English (UK)" },
  { code: "hi", label: "Hindi" },
  { code: "gu", label: "Gujarati" },
  { code: "mr", label: "Marathi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "bn", label: "Bengali" },
  { code: "ur", label: "Urdu" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (BR)" },
  { code: "ar", label: "Arabic" },
  { code: "id", label: "Indonesian" },
];

export default function CreateTemplate() {
  const navigate = useNavigate();
  const routeLocation = useLocation();

  const cloneTemplate = routeLocation.state?.cloneTemplate || null;
  const editTemplate = routeLocation.state?.editTemplate || null;
  const isEditMode = Boolean(editTemplate);
  const isCloneMode = Boolean(cloneTemplate);

  /* ---------------- BASIC INFO ---------------- */
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [language, setLanguage] = useState("en_US");
  const [wabaList, setWabaList] = useState([]);
  const [wabaId, setWabaId] = useState("");

  /* ---------------- HEADER ---------------- */
  const [showHeader, setShowHeader] = useState(false);
  const [headerType, setHeaderType] = useState("TEXT"); // TEXT | IMAGE | VIDEO | DOCUMENT | LOCATION
  const [headerText, setHeaderText] = useState("");
  const [headerHandle, setHeaderHandle] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaFileName, setMediaFileName] = useState("Sample_Document.pdf");
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // Location Header fields (Image 2)
  const [locationLatitude, setLocationLatitude] = useState("23.029583623886232");
  const [locationLongitude, setLocationLongitude] = useState("23.029583623886232");
  const [locationName, setLocationName] = useState("Stone Baked Pizza");
  const [locationAddress, setLocationAddress] = useState("Dr Vikram Sarabhai Marg");

  /* ---------------- BODY & VARIABLES ---------------- */
  const [body, setBody] = useState("");
  const bodyTextareaRef = useRef(null);
  const variables = useMemo(() => [...body.matchAll(/{{\d+}}/g)].map((v) => v[0]), [body]);
  const uniqueVars = useMemo(() => [...new Set(variables)], [variables]);
  const [samples, setSamples] = useState({});

  /* ---------------- EMOJI PICKER ---------------- */
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  /* ---------------- FOOTER & BUTTONS ---------------- */
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---------------- AUTHENTICATION TEMPLATE CONFIG ---------------- */
  const [authOtpType, setAuthOtpType] = useState("COPY_CODE"); // COPY_CODE | ONE_TAP | ZERO_TAP
  const [authCopyCodeText, setAuthCopyCodeText] = useState("Copy Code");
  const [authAutofillText, setAuthAutofillText] = useState("Autofill");
  const [authPackageName, setAuthPackageName] = useState("com.example.luckyshrub");
  const [authSignatureHash, setAuthSignatureHash] = useState("K8a%2FAINcGX7");
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [addExpiryTime, setAddExpiryTime] = useState(true);
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState(10);

  // Fetch WABA list
  useEffect(() => {
    templateService
      .getWabaIds()
      .then((res) => {
        if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
          setWabaList(res.data);
          if (!wabaId) {
            setWabaId(res.data[0].waba_id);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch WABA IDs:", err);
      });
  }, [wabaId]);

  // Prepopulate if clone or edit template passed
  useEffect(() => {
    const tpl = editTemplate || cloneTemplate;
    if (!tpl) return;

    if (cloneTemplate) {
      const baseName = (tpl.str_template_name || tpl.name || "template")
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, "");
      setTemplateName(`${baseName}_copy`.slice(0, 50));
    } else if (editTemplate) {
      setTemplateName(
        (tpl.str_template_name || tpl.name || "")
          .toLowerCase()
          .replace(/[^a-z0-9_]/g, "")
      );
    }

    if (tpl.str_category || tpl.category) {
      setCategory((tpl.str_category || tpl.category).toUpperCase());
    }
    if (tpl.str_language || tpl.language) {
      setLanguage(tpl.str_language || tpl.language);
    }
    if (tpl.str_waba_id || tpl.waba_id) {
      setWabaId(tpl.str_waba_id || tpl.waba_id);
    }

    // Extract components
    const components = tpl.obj_message?.components || tpl.components || [];
    let foundHeader = false;
    let extractedBody = tpl.str_body_text || tpl.obj_message?.body || "";
    let extractedFooter = tpl.str_footer_text || tpl.obj_message?.footer || "";
    let extractedButtons = Array.isArray(tpl.obj_message?.buttons)
      ? [...tpl.obj_message.buttons]
      : [];
    const initialSamples = {};

    components.forEach((comp) => {
      const type = (comp.type || "").toUpperCase();
      if (type === "HEADER") {
        foundHeader = true;
        const fmt = (comp.format || (comp.text ? "TEXT" : "NONE")).toUpperCase();
        if (fmt === "LOCATION") {
          setHeaderType("LOCATION");
        } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt)) {
          setHeaderType(fmt);
        } else {
          setHeaderType("TEXT");
          if (comp.text) setHeaderText(comp.text);
        }
      } else if (type === "BODY") {
        if (comp.text) extractedBody = comp.text;
        if (comp.example?.body_text && Array.isArray(comp.example.body_text[0])) {
          comp.example.body_text[0].forEach((val, idx) => {
            initialSamples[`{{${idx + 1}}}`] = String(val);
          });
        }
      } else if (type === "FOOTER") {
        if (comp.text) extractedFooter = comp.text;
      } else if (type === "BUTTONS") {
        if (Array.isArray(comp.buttons) && comp.buttons.length > 0) {
          extractedButtons = comp.buttons;
        }
      }
    });

    if (tpl.str_header_text && !foundHeader) {
      foundHeader = true;
      setHeaderType("TEXT");
      setHeaderText(tpl.str_header_text);
    }

    if (tpl.location_data || tpl.obj_message?.location_data) {
      const loc = tpl.location_data || tpl.obj_message?.location_data;
      if (loc.latitude) setLocationLatitude(loc.latitude);
      if (loc.longitude) setLocationLongitude(loc.longitude);
      if (loc.name) setLocationName(loc.name);
      if (loc.address) setLocationAddress(loc.address);
      setHeaderType("LOCATION");
      foundHeader = true;
    }

    setShowHeader(foundHeader);
    if (extractedBody) setBody(extractedBody);
    if (extractedFooter) setFooter(extractedFooter);
    if (extractedButtons.length > 0) setButtons(extractedButtons);

    if (tpl.obj_message?.samples) {
      Object.assign(initialSamples, tpl.obj_message.samples);
    }
    if (Object.keys(initialSamples).length > 0) {
      setSamples((prev) => ({ ...prev, ...initialSamples }));
    }

    // Extract Authentication template components if editing or cloning
    if ((tpl.str_category || tpl.category || "").toUpperCase() === "AUTHENTICATION") {
      components.forEach((comp) => {
        const type = (comp.type || "").toUpperCase();
        if (type === "BODY") {
          if (comp.add_security_recommendation !== undefined) {
            setAddSecurityRecommendation(Boolean(comp.add_security_recommendation));
          }
        } else if (type === "FOOTER") {
          if (comp.code_expiration_minutes) {
            setAddExpiryTime(true);
            setCodeExpirationMinutes(Number(comp.code_expiration_minutes));
          }
        } else if (type === "BUTTONS" && Array.isArray(comp.buttons)) {
          const otpBtn = comp.buttons.find((b) => (b.type || "").toUpperCase() === "OTP" || b.otp_type);
          if (otpBtn) {
            setAuthOtpType(otpBtn.otp_type || "COPY_CODE");
            if (otpBtn.text) setAuthCopyCodeText(otpBtn.text);
            if (otpBtn.autofill_text) setAuthAutofillText(otpBtn.autofill_text);
            if (otpBtn.package_name) setAuthPackageName(otpBtn.package_name);
            if (otpBtn.signature_hash) setAuthSignatureHash(otpBtn.signature_hash);
          }
        }
      });
    }
  }, [cloneTemplate, editTemplate]);

  // Button counts helper
  const buttonCounts = useMemo(() => {
    const counts = {
      custom: 0,
      visit_website: 0,
      call_phone_number: 0,
      copy_offer_code: 0,
    };
    buttons.forEach((btn) => {
      if (btn.type === "QUICK_REPLY") counts.custom++;
      else if (btn.type === "URL") counts.visit_website++;
      else if (btn.type === "PHONE_NUMBER") counts.call_phone_number++;
      else if (btn.type === "COPY_OFFER_CODE") counts.copy_offer_code++;
    });
    return counts;
  }, [buttons]);

  // Add Variable at cursor
  const handleAddVariable = () => {
    const nextIndex = uniqueVars.length + 1;
    const varTag = `{{${nextIndex}}}`;
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setBody((prev) => (prev ? `${prev} ${varTag}` : varTag));
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = body.substring(0, start);
    const after = body.substring(end);
    const newText = before + varTag + after;
    setBody(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + varTag.length, start + varTag.length);
    }, 0);
  };

  // Format Helper for Bold, Italic, Strikethrough, Monospace
  const handleWrapFormat = (symbol) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = body.substring(start, end);

    if (selectedText) {
      const wrapped = `${symbol}${selectedText}${symbol}`;
      const newText = body.substring(0, start) + wrapped + body.substring(end);
      setBody(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + wrapped.length, start + wrapped.length);
      }, 0);
    } else {
      const insert = `${symbol}${symbol}`;
      const newText = body.substring(0, start) + insert + body.substring(end);
      setBody(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + symbol.length, start + symbol.length);
      }, 0);
    }
  };

  const handleInsertEmoji = (emoji) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setBody((prev) => `${prev} ${emoji}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = body.substring(0, start) + emoji + body.substring(end);
    setBody(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  /* ---------------- MEDIA UPLOAD ---------------- */
  const uploadMedia = async (file) => {
    if (!file) return;

    setMediaFileName(file.name);
    setIsUploadingMedia(true);
    setHeaderHandle(null);
    setMediaPreview(URL.createObjectURL(file));

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (wabaId) {
        formData.append("waba_id", wabaId);
      }

      let res;
      try {
        res = await api.post("/tenant/templates/upload-media", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err1) {
        const isNotFound =
          err1?.response?.status === 404 ||
          err1?.message?.includes("404") ||
          err1?.message?.includes("not found");
        if (isNotFound) {
          try {
            res = await api.post("/templates/upload-media", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err2) {
            res = await api.post("/templatecreate/upload-media", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          }
        } else {
          throw err1;
        }
      }

      const mediaHandle =
        res?.header_handle ||
        res?.data?.header_handle ||
        res?.h ||
        res?.data?.h ||
        res?.url ||
        res?.data?.url;

      if (mediaHandle) {
        setHeaderHandle(mediaHandle);
        Swal.fire({
          icon: "success",
          title: "Media Uploaded",
          text: `Sample file "${file.name}" uploaded to WhatsApp successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        setHeaderHandle(null);
        Swal.fire({
          icon: "error",
          title: "Upload Incomplete",
          text: "WhatsApp did not return an upload session handle. Please try uploading again.",
          confirmButtonColor: "#059669",
        });
      }
    } catch (err) {
      console.error("Media upload error:", err);
      setHeaderHandle(null);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err.response?.data?.error || err.message || "Failed to upload sample media to WhatsApp.",
        confirmButtonColor: "#059669",
      });
    } finally {
      setIsUploadingMedia(false);
    }
  };

  /* ---------------- SUBMIT TEMPLATE ---------------- */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!templateName.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Template Name Required",
        text: "Please provide a valid template name.",
        confirmButtonColor: "#059669",
      });
    }

    if (!/^[a-z0-9_]+$/.test(templateName)) {
      return Swal.fire({
        icon: "warning",
        title: "Invalid Template Name",
        text: "Template name can only contain lowercase letters, numbers, and underscores.",
        confirmButtonColor: "#059669",
      });
    }

    let templatePayload;

    if (category === "AUTHENTICATION") {
      if (authOtpType === "COPY_CODE" && !authCopyCodeText.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Copy Code Text Required",
          text: "Please provide a valid button text (e.g. Copy Code).",
          confirmButtonColor: "#059669",
        });
      }

      if (authOtpType === "ONE_TAP" || authOtpType === "ZERO_TAP") {
        if (!authPackageName.trim()) {
          return Swal.fire({
            icon: "warning",
            title: "Package Name Required",
            text: "Please enter your Android app package name (e.g. com.example.luckyshrub).",
            confirmButtonColor: "#059669",
          });
        }
        if (!authSignatureHash.trim()) {
          return Swal.fire({
            icon: "warning",
            title: "Signature Hash Required",
            text: "Please enter your app signing key signature hash (e.g. K8a%2FAINcGX7).",
            confirmButtonColor: "#059669",
          });
        }
      }

      if (addExpiryTime) {
        const exp = Number(codeExpirationMinutes);
        if (isNaN(exp) || exp < 1 || exp > 90) {
          return Swal.fire({
            icon: "warning",
            title: "Invalid Expiry Time",
            text: "Code expiration time must be between 1 and 90 minutes.",
            confirmButtonColor: "#059669",
          });
        }
      }

      const authButtons = [
        authOtpType === "COPY_CODE"
          ? {
              type: "OTP",
              otp_type: "COPY_CODE",
              text: authCopyCodeText.trim() || "Copy Code",
            }
          : authOtpType === "ONE_TAP"
          ? {
              type: "OTP",
              otp_type: "ONE_TAP",
              text: authCopyCodeText.trim() || "Copy Code",
              autofill_text: authAutofillText.trim() || "Autofill",
              package_name: authPackageName.trim(),
              signature_hash: authSignatureHash.trim(),
            }
          : {
              type: "OTP",
              otp_type: "ZERO_TAP",
              text: authCopyCodeText.trim() || "Copy Code",
              autofill_text: authAutofillText.trim() || "Autofill",
              package_name: authPackageName.trim(),
              signature_hash: authSignatureHash.trim(),
              zero_tap_terms_accepted: true,
            },
      ];

      const authComponents = [
        {
          type: "BODY",
          add_security_recommendation: Boolean(addSecurityRecommendation),
        },
        ...(addExpiryTime
          ? [
              {
                type: "FOOTER",
                code_expiration_minutes: Number(codeExpirationMinutes) || 10,
              },
            ]
          : []),
        {
          type: "BUTTONS",
          buttons: authButtons,
        },
      ];

      templatePayload = {
        name: templateName.trim(),
        str_template_name: templateName.trim(),
        language,
        str_language: language,
        category: "AUTHENTICATION",
        str_category: "AUTHENTICATION",
        waba_id: wabaId,
        str_waba_id: wabaId,
        components: authComponents,
        add_security_recommendation: Boolean(addSecurityRecommendation),
        code_expiration_minutes: addExpiryTime ? Number(codeExpirationMinutes) : null,
        otp_type: authOtpType,
        str_body_text: `{{1}} is your verification code.${addSecurityRecommendation ? " For your security, do not share this code." : ""}`,
        str_footer_text: addExpiryTime ? `This code expires in ${codeExpirationMinutes} minutes.` : "",
        obj_message: {
          components: authComponents,
          body: `{{1}} is your verification code.${addSecurityRecommendation ? " For your security, do not share this code." : ""}`,
          footer: addExpiryTime ? `This code expires in ${codeExpirationMinutes} minutes.` : "",
          buttons: authButtons,
        },
        str_status: isEditMode ? (editTemplate?.str_status || "PENDING") : "PENDING",
      };
    } else {
      if (!body.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Body Required",
          text: "Please enter the body text for your message template.",
          confirmButtonColor: "#059669",
        });
      }

      // Check variable sequentiality
      if (uniqueVars.length > 0) {
        const varNumbers = uniqueVars
          .map((v) => parseInt(v.replace(/[{}]/g, "")))
          .sort((a, b) => a - b);
        let isSeq = true;
        for (let i = 0; i < varNumbers.length; i++) {
          if (varNumbers[i] !== i + 1) {
            isSeq = false;
            break;
          }
        }
        if (!isSeq) {
          return Swal.fire({
            icon: "warning",
            title: "Invalid Variables",
            text: "Body variables must be sequential starting from {{1}} (e.g. {{1}}, {{2}}, {{3}}).",
            confirmButtonColor: "#059669",
          });
        }

        // Check for missing samples
        const missingSamples = uniqueVars.filter((v) => !samples[v]?.trim());
        if (missingSamples.length > 0) {
          return Swal.fire({
            icon: "warning",
            title: "Sample Values Required",
            text: `Please enter sample values for ${missingSamples.join(", ")} so WhatsApp reviewers can approve your template.`,
            confirmButtonColor: "#059669",
          });
        }
      }

      // Build components
      const components = [];

      if (showHeader) {
        if (headerType === "TEXT") {
          if (!headerText.trim()) {
            return Swal.fire({
              icon: "warning",
              title: "Header Text Required",
              text: "Please enter your header text or turn off 'Show header'.",
              confirmButtonColor: "#059669",
            });
          }
          components.push({
            type: "HEADER",
            format: "TEXT",
            text: headerText.trim(),
          });
        } else if (headerType === "LOCATION") {
          if (!locationName.trim() || !locationAddress.trim()) {
            return Swal.fire({
              icon: "warning",
              title: "Location Fields Required",
              text: "Please provide Location Name and Location Address.",
              confirmButtonColor: "#059669",
            });
          }
          components.push({
            type: "HEADER",
            format: "LOCATION",
          });
        } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType)) {
          if (!headerHandle && !mediaFileName) {
            return Swal.fire({
              icon: "warning",
              title: "Sample Media Required",
              text: `Please upload a sample ${headerType.toLowerCase()} file for your header so WhatsApp reviewers can approve your template.`,
              confirmButtonColor: "#059669",
            });
          }
          components.push({
            type: "HEADER",
            format: headerType,
            ...(headerHandle && { example: { header_handle: [headerHandle] } }),
          });
        }
      }

      // Body component
      components.push({
        type: "BODY",
        text: body,
        ...(uniqueVars.length > 0 && {
          example: {
            body_text: [uniqueVars.map((v) => samples[v] || "sample")],
          },
        }),
      });

      // Footer component
      if (footer.trim()) {
        components.push({
          type: "FOOTER",
          text: footer.trim(),
        });
      }

      // Buttons component
      if (buttons.length > 0) {
        // Validate buttons before creating payload
        for (let i = 0; i < buttons.length; i++) {
          const btn = buttons[i];
          if (!btn.text?.trim()) {
            return Swal.fire({
              icon: "warning",
              title: "Button Text Required",
              text: `Please enter the button title/text for button #${i + 1}.`,
              confirmButtonColor: "#059669",
            });
          }
          if (btn.type === "PHONE_NUMBER") {
            const raw = (btn.phone_number || "").trim().replace(/[\s\-\(\)]/g, "");
            if (!raw) {
              return Swal.fire({
                icon: "warning",
                title: "Phone Number Required",
                text: `Please enter a valid phone number with country code for "${btn.text || 'Call'}" button (e.g. +919876543210).`,
                confirmButtonColor: "#059669",
              });
            }
          }
          if (btn.type === "URL" && !(btn.url || "").trim()) {
            return Swal.fire({
              icon: "warning",
              title: "Website URL Required",
              text: `Please enter a website URL for "${btn.text || 'Visit Website'}" button.`,
              confirmButtonColor: "#059669",
            });
          }
          if (btn.type === "COPY_OFFER_CODE" && !(btn.code || "").trim()) {
            return Swal.fire({
              icon: "warning",
              title: "Offer Code Required",
              text: `Please enter an offer/coupon code for "${btn.text || 'Copy Code'}" button.`,
              confirmButtonColor: "#059669",
            });
          }
        }

        const formattedButtons = buttons.map((btn) => {
          const btnObj = { type: btn.type, text: btn.text.trim() };
          if (btn.type === "URL") {
            let u = (btn.url || "").trim();
            if (!/^https?:\/\//i.test(u) && !u.includes("{{")) u = "https://" + u;
            btnObj.url = u;
          }
          if (btn.type === "PHONE_NUMBER") {
            let p = (btn.phone_number || "").trim().replace(/[\s\-\(\)]/g, "");
            if (p && !p.startsWith("+")) {
              if (p.length === 10) p = "+91" + p;
              else if (p.length === 12 && p.startsWith("91")) p = "+" + p;
              else p = "+" + p;
            }
            btnObj.phone_number = p;
          }
          if (btn.type === "COPY_OFFER_CODE") btnObj.code = (btn.code || "").trim();
          return btnObj;
        });
        components.push({
          type: "BUTTONS",
          buttons: formattedButtons,
        });
      }

      templatePayload = {
        name: templateName.trim(),
        str_template_name: templateName.trim(),
        language,
        str_language: language,
        category,
        str_category: category,
        waba_id: wabaId,
        str_waba_id: wabaId,
        components,
        str_body_text: body,
        str_header_text: headerType === "TEXT" ? headerText : (headerType === "LOCATION" ? locationName : ""),
        str_footer_text: footer,
        location_data: showHeader && headerType === "LOCATION" ? {
          latitude: locationLatitude,
          longitude: locationLongitude,
          name: locationName,
          address: locationAddress,
        } : null,
        str_status: isEditMode ? (editTemplate?.str_status || "PENDING") : "PENDING",
      };
    }

    setIsSubmitting(true);
    Swal.fire({
      title: isEditMode ? "Updating Template..." : "Submitting Template...",
      html: `Submitting <b>${templateName}</b> for review...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      let res;
      if (isEditMode) {
        const editId =
          editTemplate.int_template_id || editTemplate._id || editTemplate.id;
        res = await templateService.edit(editId, templatePayload);
      } else {
        res = await templateService.create(templatePayload);
      }

      if (res && (res.success || !res.error)) {
        await Swal.fire({
          icon: "success",
          title: isEditMode ? "Template Updated!" : "Template Created!",
          text: `Template "${templateName}" has been successfully saved.`,
          timer: 2000,
          showConfirmButton: false,
        });
        navigate("/templates");
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Error",
          text: res?.error || res?.message || "Failed to save the template.",
          confirmButtonColor: "#059669",
        });
      }
    } catch (err) {
      console.error("Template submission error:", err);
      Swal.fire({
        icon: "error",
        title: "Submission Error",
        text: err.response?.data?.error || err.message || "Failed to submit template.",
        confirmButtonColor: "#059669",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto space-y-6 pb-16">
      {/* Top Breadcrumb & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/templates")}
            className="inline-flex items-center text-xs font-semibold text-gray-500 hover:text-green-600 mb-2 transition-colors cursor-pointer"
          >
            ← Back to Templates
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? `Edit Template: ${templateName}` : isCloneMode ? `Clone Template: ${templateName}` : "Create New Template"}
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
            Configure your message template and preview how customers see it on WhatsApp.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* LEFT COLUMN: FORM */}
        <div className="xl:col-span-2 space-y-6">
          {/* 1. Basic Template Setup (Image 2 - Single Clean Dropdown for Category) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
              Set up your template
            </h2>

            {/* Template Name, Category, Language (Image 2) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => {
                    const val = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "");
                    setTemplateName(val);
                  }}
                  maxLength={512}
                  placeholder="e.g. seasonal_sale_01"
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Name can only have lowercase letters, numbers, and underscores.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Select category option for your template.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Language <span className="text-red-500">*</span>
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                >
                  {META_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Select the language option for your template.
                </p>
              </div>
            </div>

            {/* WABA Account */}
            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                WABA Account <span className="text-red-500">*</span>
              </label>
              <select
                value={wabaId}
                onChange={(e) => setWabaId(e.target.value)}
                className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer font-mono"
              >
                {wabaList.length === 0 ? (
                  <option value="">Default WABA</option>
                ) : (
                  wabaList.map((w) => (
                    <option key={w.waba_id} value={w.waba_id}>
                      WABA: {w.waba_id} {w.phone_number ? `(${w.phone_number})` : ""}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* ── AUTHENTICATION TEMPLATE CONFIGURATION (When Category = AUTHENTICATION) ── */}
          {category === "AUTHENTICATION" ? (
            <>
              {/* Code delivery setup Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-5">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Code delivery setup
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                      Authentication • OTP
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Choose how customers send the code from WhatsApp to your app. Edits to this section won't require review or count towards edit limits.
                  </p>
                </div>

                {/* Delivery Options */}
                <div className="space-y-3">
                  {[
                    {
                      id: "ZERO_TAP",
                      title: "Zero-tap auto-fill",
                      desc: "This is recommended as the easiest option for your customers. Zero-tap will automatically send code without requiring your customer to tap a button. An auto-fill or copy code message will be sent if zero-tap and auto-fill aren't possible.",
                    },
                    {
                      id: "ONE_TAP",
                      title: "One-tap auto-fill",
                      desc: "The code sends to your app when customers tap the button. A copy code message will be sent if auto-fill isn't possible.",
                    },
                    {
                      id: "COPY_CODE",
                      title: "Copy code",
                      desc: "Basic authentication with quick setup. Your customers copy and paste the code into your app.",
                    },
                  ].map((option) => {
                    const isSelected = authOtpType === option.id;
                    return (
                      <div
                        key={option.id}
                        onClick={() => setAuthOtpType(option.id)}
                        className={`flex items-start gap-3.5 p-4 rounded-xl border transition-all cursor-pointer ${
                          isSelected
                            ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-500 shadow-xs ring-1 ring-blue-500"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                        }`}
                      >
                        <input
                          type="radio"
                          name="authOtpType"
                          checked={isSelected}
                          onChange={() => setAuthOtpType(option.id)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {option.title}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                            {option.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Fields for COPY_CODE */}
                {authOtpType === "COPY_CODE" && (
                  <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Copy Code button text <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={authCopyCodeText}
                      onChange={(e) => setAuthCopyCodeText(e.target.value)}
                      maxLength={25}
                      placeholder="Copy Code"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                    <div className="flex justify-between items-center text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                      <span>Label shown on customer's WhatsApp button</span>
                      <span>{authCopyCodeText.length}/25</span>
                    </div>
                  </div>
                )}

                {/* Fields for ONE_TAP & ZERO_TAP */}
                {(authOtpType === "ONE_TAP" || authOtpType === "ZERO_TAP") && (
                  <div className="pt-3 border-t border-gray-100 dark:border-gray-700 space-y-4">
                    <div className="bg-blue-50/70 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3.5 text-xs text-blue-800 dark:text-blue-300">
                      <p className="leading-relaxed">
                        {authOtpType === "ONE_TAP"
                          ? "A one-tap autofill button automatically loads and passes your app the one-time password or code. Currently supported on Android clients where a customer has also installed your consumer app with app signing code changes implemented. On unsupported devices Copy Code will be available instead."
                          : "Zero-tap will automatically send the code without requiring the customer to tap a button on supported Android devices. On unsupported devices auto-fill or copy code will be used."}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Copy Code button text <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={authCopyCodeText}
                          onChange={(e) => setAuthCopyCodeText(e.target.value)}
                          maxLength={25}
                          placeholder="Copy Code"
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                          <span>Fallback button label</span>
                          <span>{authCopyCodeText.length}/25</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          One Tap / Autofill button text <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={authAutofillText}
                          onChange={(e) => setAuthAutofillText(e.target.value)}
                          maxLength={25}
                          placeholder="Autofill"
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                          <span>Autofill button label</span>
                          <span>{authAutofillText.length}/25</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Android package name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={authPackageName}
                          onChange={(e) => setAuthPackageName(e.target.value)}
                          placeholder="com.example.luckyshrub"
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          Your Android app's package name
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Signature Hash <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={authSignatureHash}
                          onChange={(e) => setAuthSignatureHash(e.target.value)}
                          placeholder="K8a%2FAINcGX7"
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                          Your app signing key hash
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Content Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Content
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Content for authentication message templates can't be edited. You can add additional content from the options below.
                  </p>
                </div>

                {/* WhatsApp Authentication Standard Notice */}
                <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 border border-gray-200 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🔐</span>
                    <span>Standard OTP Body: <b>{"{{1}}"} is your verification code.</b></span>
                  </div>
                  <span className="text-[11px] text-gray-500">Locked by Meta</span>
                </div>

                <div className="space-y-4 pt-1">
                  {/* Security Recommendation Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={addSecurityRecommendation}
                      onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                    />
                    <div>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        Add security recommendation
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Appends "For your security, do not share this code." to the message body.
                      </p>
                    </div>
                  </label>

                  {/* Expiry Time Checkbox */}
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={addExpiryTime}
                        onChange={(e) => setAddExpiryTime(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          Add expiry time for the code
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Appends code expiration disclaimer to the message footer.
                        </p>
                      </div>
                    </label>

                    {addExpiryTime && (
                      <div className="ml-7 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-200 dark:border-gray-600 max-w-sm">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Expires in
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={90}
                            value={codeExpirationMinutes}
                            onChange={(e) =>
                              setCodeExpirationMinutes(
                                Math.max(1, Math.min(90, Number(e.target.value) || 1))
                              )
                            }
                            className="w-24 px-3 py-1.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-center font-bold"
                          />
                          <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                            minutes (1 - 90)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* 2. Header Section (Image 2) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            {/* Toggle switch for Show header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHeader}
                    onChange={(e) => setShowHeader(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    Show header
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Add a title or choose which type of media you'll use for this header.
                  </p>
                </div>
              </div>
            </div>

            {/* Choose Header Type (5 Cards: Text, Image, Video, Document, Location) */}
            {showHeader && (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Choose header type
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[
                      {
                        id: "TEXT",
                        label: "Text",
                        icon: (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-bold text-gray-700 dark:text-gray-300 text-lg">
                            T
                          </div>
                        ),
                      },
                      {
                        id: "IMAGE",
                        label: "Image",
                        icon: (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300">
                            🖼️
                          </div>
                        ),
                      },
                      {
                        id: "VIDEO",
                        label: "Video",
                        icon: (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300">
                            ▶️
                          </div>
                        ),
                      },
                      {
                        id: "DOCUMENT",
                        label: "Document",
                        icon: (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300">
                            📄
                          </div>
                        ),
                      },
                      {
                        id: "LOCATION",
                        label: "Location",
                        icon: (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400">
                            📍
                          </div>
                        ),
                      },
                    ].map((h) => {
                      const isSel = headerType === h.id;
                      return (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => setHeaderType(h.id)}
                          className={`flex flex-col items-center justify-center p-3.5 rounded-xl border transition-all cursor-pointer ${
                            isSel
                              ? "border-2 border-blue-600 bg-blue-50/60 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold shadow-sm"
                              : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                          }`}
                        >
                          <div className="mb-2">{h.icon}</div>
                          <span className="text-xs">{h.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* When Location is selected (Image 2) */}
                {headerType === "LOCATION" && (
                  <div className="bg-gray-50/80 dark:bg-gray-700/40 p-4 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Location Latitude <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={locationLatitude}
                          onChange={(e) => setLocationLatitude(e.target.value)}
                          placeholder="e.g. 23.029583623886232"
                          className="w-full px-3.5 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          Please provide the location latitude.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Location Longitude <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={locationLongitude}
                          onChange={(e) => setLocationLongitude(e.target.value)}
                          placeholder="e.g. 23.029583623886232"
                          className="w-full px-3.5 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          Please provide the location longitude.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Location Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={locationName}
                          onChange={(e) => setLocationName(e.target.value)}
                          placeholder="e.g. Stone Baked Pizza"
                          className="w-full px-3.5 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          Text that will appear immediately below the generic map at the top of the message.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Location Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={locationAddress}
                          onChange={(e) => setLocationAddress(e.target.value)}
                          placeholder="e.g. Dr Vikram Sarabhai Marg"
                          className="w-full px-3.5 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          Address that will appear after the name value, below the generic map at the top of the message.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* When Text is selected */}
                {headerType === "TEXT" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Header Text
                    </label>
                    <input
                      type="text"
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      maxLength={60}
                      placeholder="e.g. Special Announcement"
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <div className="flex justify-between items-center mt-1 text-[11px] text-gray-500">
                      <span>Header supports a single variable like {'{{1}}'}.</span>
                      <span>{headerText.length}/60 characters</span>
                    </div>
                  </div>
                )}

                {/* When Image is selected - Clean card matching Location style */}
                {headerType === "IMAGE" && (
                  <div className="bg-gray-50/80 dark:bg-gray-700/40 p-4 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      {/* Image Thumbnail Preview */}
                      <div className="w-28 h-24 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 bg-gray-200 shrink-0 flex items-center justify-center">
                        <img
                          src={
                            mediaPreview ||
                            "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=500&auto=format&fit=crop&q=70"
                          }
                          alt="Header sample preview"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 w-full space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            Upload Sample Image
                          </label>
                          {mediaPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setMediaPreview(null);
                                setHeaderHandle(null);
                              }}
                              className="text-[11px] text-red-500 hover:underline cursor-pointer"
                            >
                              Reset to default
                            </button>
                          )}
                        </div>
                        <input
                          type="file"
                          id="image-header-upload"
                          onChange={(e) => uploadMedia(e.target.files[0])}
                          accept="image/jpeg,image/png"
                          className="hidden"
                        />
                        <label
                          htmlFor="image-header-upload"
                          className="inline-flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer shadow-xs transition-colors"
                        >
                          {isUploadingMedia ? "Uploading image..." : "Choose Image File"}
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Supports JPG, PNG (max 5MB). The selected image will appear at the top of your message.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* When Video is selected - Clean card matching Location style */}
                {headerType === "VIDEO" && (
                  <div className="bg-gray-50/80 dark:bg-gray-700/40 p-4 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      {/* Video Thumbnail Preview */}
                      <div className="w-28 h-24 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 bg-slate-900 shrink-0 flex flex-col items-center justify-center text-white">
                        {mediaPreview ? (
                          <video src={mediaPreview} className="w-full h-full object-cover" />
                        ) : (
                          <>
                            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs mb-1">
                              ▶
                            </div>
                            <span className="text-[9px] text-gray-300">Sample Video</span>
                          </>
                        )}
                      </div>

                      <div className="flex-1 w-full space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            Upload Sample Video
                          </label>
                          {mediaPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setMediaPreview(null);
                                setHeaderHandle(null);
                              }}
                              className="text-[11px] text-red-500 hover:underline cursor-pointer"
                            >
                              Reset to default
                            </button>
                          )}
                        </div>
                        <input
                          type="file"
                          id="video-header-upload"
                          onChange={(e) => uploadMedia(e.target.files[0])}
                          accept="video/mp4,video/3gpp"
                          className="hidden"
                        />
                        <label
                          htmlFor="video-header-upload"
                          className="inline-flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer shadow-xs transition-colors"
                        >
                          {isUploadingMedia ? "Uploading video..." : "Choose Video File"}
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Supports MP4, 3GPP (max 16MB). Customers can tap to watch directly inside WhatsApp.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* When Document is selected - Clean card matching Location style */}
                {headerType === "DOCUMENT" && (
                  <div className="bg-gray-50/80 dark:bg-gray-700/40 p-4 rounded-xl border border-gray-200 dark:border-gray-600 space-y-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      {/* Document Card Preview */}
                      <div className="w-28 h-24 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shrink-0 flex flex-col items-center justify-center p-2">
                        <div className="w-8 h-8 rounded bg-red-100 text-red-600 font-bold text-xs flex items-center justify-center mb-1">
                          PDF
                        </div>
                        <span className="text-[9px] text-gray-600 dark:text-gray-300 font-medium truncate max-w-full">
                          {mediaFileName}
                        </span>
                      </div>

                      <div className="flex-1 w-full space-y-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">
                            Document Name / File Title
                          </label>
                          <input
                            type="text"
                            value={mediaFileName}
                            onChange={(e) => setMediaFileName(e.target.value)}
                            placeholder="e.g. Product_Catalog.pdf"
                            className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div className="flex items-center gap-3 pt-1">
                          <input
                            type="file"
                            id="document-header-upload"
                            onChange={(e) => uploadMedia(e.target.files[0])}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                            className="hidden"
                          />
                          <label
                            htmlFor="document-header-upload"
                            className="inline-flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer shadow-xs transition-colors"
                          >
                            {isUploadingMedia ? "Uploading document..." : "Choose Document File"}
                          </label>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Supports PDF, Word, Excel documents (max 100MB).
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. Body Section with Toolbar, Full Emoji Picker & Variable Inserter (Image 3) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                  Body <span className="text-red-500">*</span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Make your messages personal using variables like {"{{name}}"} and get more replies!
                </p>
              </div>

              {/* + Add Variable button (Image 3) */}
              <button
                type="button"
                onClick={handleAddVariable}
                className="inline-flex items-center px-3.5 py-1.5 border border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/40 font-semibold text-xs rounded-lg transition-colors cursor-pointer"
              >
                <span className="text-base mr-1.5 leading-none">⊕</span> Add Variable
              </button>
            </div>

            {/* Formatting Toolbar (Image 3) with Full Emoji Picker */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100/80 dark:bg-gray-700/60 rounded-t-xl border border-b-0 border-gray-300 dark:border-gray-600 text-xs relative">
              <div className="flex items-center space-x-2">
                {/* Emoji Picker Button & Popup */}
                <div className="relative" ref={emojiPickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                    className={`p-1 rounded text-sm transition-all cursor-pointer ${
                      showEmojiPicker
                        ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        : "hover:bg-white dark:hover:bg-gray-600"
                    }`}
                    title="Choose Emoji"
                  >
                    😊
                  </button>

                  {showEmojiPicker && (
                    <div className="absolute left-0 top-full mt-2 z-50 shadow-2xl rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          handleInsertEmoji(emojiData.emoji);
                          setShowEmojiPicker(false);
                        }}
                        autoFocusSearch={false}
                        searchPlaceHolder="Search emojis..."
                        width={320}
                        height={380}
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                </div>

                <div className="h-4 w-[1px] bg-gray-300 dark:bg-gray-600" />
                <button
                  type="button"
                  onClick={() => handleWrapFormat("*")}
                  className="px-2 py-0.5 hover:bg-white dark:hover:bg-gray-600 rounded font-bold transition-colors cursor-pointer"
                  title="Bold (*text*)"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => handleWrapFormat("_")}
                  className="px-2 py-0.5 hover:bg-white dark:hover:bg-gray-600 rounded italic transition-colors cursor-pointer"
                  title="Italic (_text_)"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => handleWrapFormat("~")}
                  className="px-2 py-0.5 hover:bg-white dark:hover:bg-gray-600 rounded line-through transition-colors cursor-pointer"
                  title="Strikethrough (~text~)"
                >
                  S
                </button>
                <button
                  type="button"
                  onClick={() => handleWrapFormat("```")}
                  className="px-2 py-0.5 hover:bg-white dark:hover:bg-gray-600 rounded font-mono text-[11px] transition-colors cursor-pointer"
                  title="Monospace (```text```)"
                >
                  Mono
                </button>
              </div>

              {/* Character counter (Image 3) */}
              <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                {body.length}/1024
              </div>
            </div>

            {/* Textarea */}
            <textarea
              ref={bodyTextareaRef}
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1024}
              placeholder="Template Message..."
              className="w-full px-3.5 py-3 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-b-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-y"
            />

            {/* AI Enhancement notice (Image 3) */}
            <div className="flex items-center space-x-2 text-xs text-blue-600 dark:text-blue-400 pt-1">
              <span className="text-sm">🎯</span>
              <span>Maximise your content's impact with our AI tool</span>
              <button
                type="button"
                onClick={() => {
                  Swal.fire({
                    title: "AI Message Assistant",
                    text: "Ensure clear messaging, polite tone, and no promotional spam words to maximize WhatsApp approval speed.",
                    icon: "info",
                    confirmButtonColor: "#059669",
                  });
                }}
                className="font-semibold underline hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer ml-1"
              >
                Enhance now
              </button>
            </div>

            {/* Variable Samples input (Image 3) */}
            {uniqueVars.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Variable Samples (Required for WhatsApp review)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {uniqueVars.map((v) => (
                    <div key={v} className="flex items-center space-x-2">
                      <span className="w-14 text-center font-mono font-bold text-xs bg-gray-100 dark:bg-gray-700 text-blue-600 px-2.5 py-2 rounded-lg border border-gray-200 dark:border-gray-600">
                        {v}
                      </span>
                      <input
                        type="text"
                        placeholder={`Sample value for ${v}`}
                        value={samples[v] || ""}
                        onChange={(e) =>
                          setSamples({ ...samples, [v]: e.target.value })
                        }
                        className="flex-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 4. Footer Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Footer <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <span className="text-[11px] text-gray-500">{footer.length}/60 characters</span>
            </div>
            <input
              type="text"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              maxLength={60}
              placeholder="e.g. Reply STOP to unsubscribe."
              className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* 5. Buttons Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                  Buttons <span className="text-gray-400 font-normal">(Optional)</span>
                </h3>
                <p className="text-xs text-gray-500">
                  Total buttons: {buttons.length}/10
                </p>
              </div>
            </div>

            {/* Button Adders */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <button
                type="button"
                onClick={() =>
                  setButtons([...buttons, { type: "QUICK_REPLY", text: "" }])
                }
                disabled={buttons.length >= 10}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                + Custom
              </button>

              <button
                type="button"
                onClick={() =>
                  setButtons([
                    ...buttons,
                    { type: "URL", text: "", url: "" },
                  ])
                }
                disabled={buttons.length >= 10 || buttonCounts.visit_website >= 2}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                + Visit Website {buttonCounts.visit_website >= 2 ? "(Max)" : `(${buttonCounts.visit_website}/2)`}
              </button>

              <button
                type="button"
                onClick={() =>
                  setButtons([
                    ...buttons,
                    { type: "PHONE_NUMBER", text: "", phone_number: "" },
                  ])
                }
                disabled={buttons.length >= 10 || buttonCounts.call_phone_number >= 1}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                + Call Phone {buttonCounts.call_phone_number >= 1 ? "(Max)" : `(${buttonCounts.call_phone_number}/1)`}
              </button>

              <button
                type="button"
                onClick={() =>
                  setButtons([
                    ...buttons,
                    { type: "COPY_OFFER_CODE", text: "", code: "" },
                  ])
                }
                disabled={buttons.length >= 10 || buttonCounts.copy_offer_code >= 1}
                className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                + Copy Offer Code {buttonCounts.copy_offer_code >= 1 ? "(Max)" : `(${buttonCounts.copy_offer_code}/1)`}
              </button>
            </div>

            {/* Render Configured Buttons */}
            {buttons.length > 0 && (
              <div className="space-y-3 pt-2">
                {buttons.map((btn, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-700/30 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                        {btn.type === "QUICK_REPLY" && "🔘 Custom Quick Reply"}
                        {btn.type === "URL" && "🌐 Website URL"}
                        {btn.type === "PHONE_NUMBER" && "📞 Phone Call"}
                        {btn.type === "COPY_OFFER_CODE" && "📋 Copy Offer Code"}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setButtons(buttons.filter((_, i) => i !== index))
                        }
                        className="text-xs text-red-600 hover:text-red-800 font-semibold cursor-pointer"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          placeholder="Button text (max 25 chars)"
                          maxLength={25}
                          value={btn.text}
                          onChange={(e) => {
                            const updated = [...buttons];
                            updated[index].text = e.target.value;
                            setButtons(updated);
                          }}
                          className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>

                      {btn.type === "URL" && (
                        <div>
                          <input
                            type="text"
                            placeholder="https://example.com"
                            value={btn.url || ""}
                            onChange={(e) => {
                              const updated = [...buttons];
                              updated[index].url = e.target.value;
                              setButtons(updated);
                            }}
                            className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      )}

                      {btn.type === "PHONE_NUMBER" && (
                        <div>
                          <input
                            type="tel"
                            placeholder="e.g. +919876543210 (with country code)"
                            value={btn.phone_number || ""}
                            onChange={(e) => {
                              const updated = [...buttons];
                              updated[index].phone_number = e.target.value;
                              setButtons(updated);
                            }}
                            onBlur={(e) => {
                              let val = (e.target.value || "").trim().replace(/[\s\-\(\)]/g, "");
                              if (val && !val.startsWith("+")) {
                                if (val.length === 10) val = "+91" + val;
                                else if (val.length === 12 && val.startsWith("91")) val = "+" + val;
                                else val = "+" + val;
                                const updated = [...buttons];
                                updated[index].phone_number = val;
                                setButtons(updated);
                              }
                            }}
                            className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                            Must include country code with leading '+' (e.g. +91 for India).
                          </p>
                        </div>
                      )}

                      {btn.type === "COPY_OFFER_CODE" && (
                        <div>
                          <input
                            type="text"
                            placeholder="e.g. DISCOUNT20"
                            value={btn.code || ""}
                            onChange={(e) => {
                              const updated = [...buttons];
                              updated[index].code = e.target.value;
                              setButtons(updated);
                            }}
                            className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          
            </>
          )}

          {/* 6. Ready to Submit Card */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                Ready to submit?
              </h3>
              <p className="text-xs text-gray-500">
                WhatsApp reviews all message templates before they can be sent to customers.
              </p>
            </div>
            <div className="flex items-center space-x-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => navigate("/templates")}
                className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-xs sm:text-sm font-semibold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-8 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl font-bold text-xs sm:text-sm shadow-sm transition-all disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : isEditMode ? (
                  "Update Template"
                ) : (
                  "Submit Template"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: WHATSAPP LIVE PREVIEW */}
        <div className="xl:col-span-1">
          <div className="sticky top-6 space-y-3">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">
              WhatsApp Preview
            </h3>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex justify-center items-center">
              <WhatsAppPreview
                templateName={templateName || "template_preview"}
                category={category}
                isAuthentication={category === "AUTHENTICATION"}
                headerType={showHeader ? headerType : "NONE"}
                headerText={headerText}
                headerPreview={mediaPreview || headerHandle}
                mediaFileName={mediaFileName}
                locationData={{
                  latitude: locationLatitude,
                  longitude: locationLongitude,
                  name: locationName,
                  address: locationAddress,
                }}
                bodyText={body}
                footerText={footer}
                buttons={buttons}
                samples={samples}
                authOtpType={authOtpType}
                authCopyCodeText={authCopyCodeText}
                authAutofillText={authAutofillText}
                addSecurityRecommendation={addSecurityRecommendation}
                addExpiryTime={addExpiryTime}
                codeExpirationMinutes={codeExpirationMinutes}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
